<script setup lang="ts">
import { computed } from 'vue'

export interface Props {
  asBtn?: boolean
  asRadio?: boolean
  asSwitch?: boolean
  disabled?: boolean
  hasIcon?: boolean
  icon?: string
  icons?: [string, string]
  indeterminate?: boolean
  loading?: boolean
  value?: boolean | number | string | object
}

const props = withDefaults(defineProps<Props>(), {
  hasIcon: true,
})

const emit = defineEmits(['input', 'click'])

const l_icon = computed(() => {
  if (props.asRadio) {
    return props.value ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'
  }
  if (props.icon) {
    return props.icon
  }
  if (props.icons) {
    return props.value ? props.icons[1] : props.icons[0]
  }
  return props.indeterminate
    ? 'mdi-minus-box'
    : props.value
      ? 'mdi-checkbox-marked'
      : 'mdi-checkbox-blank-outline'
})

const onChange = (e: Event, checked: boolean) => {
  if (props.disabled) return
  emit('input', checked)
  emit('click', e)
}
</script>

<template>
  <div
    v-if="asSwitch"
    class="checkbox-switch"
    :class="[{ 'checkbox-disabled': disabled }, value ? 'checkbox-on' : 'checkbox-off']"
    @click="e => onChange(e, !value)"
    @keydown.enter="e => onChange(e, !value)"
  >
    <div
      v-if="hasIcon"
      class="checkbox-switch_body"
    >
      <div class="checkbox-switch_circle"></div>
    </div>
    <div
      v-if="$slots.default"
      class="checkbox-switch_label"
    >
      <slot />
    </div>
  </div>

  <div
    v-else-if="asBtn"
    class="checkbox-btn"
    :class="[
      {
        'checkbox-btn_active': value,
      },
      { 'checkbox-disabled': disabled },
      { 'checkbox-loading': loading },
    ]"
    @click="e => onChange(e, !value)"
  >
    <slot />
  </div>

  <div
    v-else
    class="checkbox-custom"
    :class="value ? 'checkbox-on' : 'checkbox-off'"
    @click="e => onChange(e, !value)"
  >
    <div
      v-if="hasIcon"
      class="checkbox-icon mdi csst csst_nm"
      :class="[l_icon, { 'checkbox-disabled': disabled }]"
      :style="`color:${value || indeterminate ? 'var(--clr-enabled)' : 'var(--clr-disabled)'}`"
    >
      <slot :name="value ? 'icon_tooltip_on' : 'icon_tooltip_off'" />
    </div>
    <div
      v-if="$slots.default"
      class="checkbox-custom_label"
    >
      <slot />
      <slot name="label_tooltip" />
    </div>
    <slot
      name="content"
      :value="value"
    />
  </div>
</template>

<style lang="scss">
.checkbox-off-hover_icon-visible {
  .checkbox-on {
    .checkbox-icon {
      opacity: 0;
      transition: 0.22s;
    }
  }
  &:hover {
    .checkbox-icon {
      opacity: 1;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.checkbox-disabled {
  pointer-events: none;
  --clr: var(--clr-grey-2) !important;
  --clr-grey-2: var(--clr-black-8) !important;
}

.checkbox-custom {
  display: flex;
  align-items: center;
  --clr-disabled: var(--clr-white-0);
  --clr-enabled: var(--clr-primary);
  --label-size: 1em;
  grid-gap: 0.5em;
  --icon-size: 1.5em;
  --clr-label: var(--clr-grey-2);
  &_label {
    width: 100%;
    font-size: var(--label-size);
    text-transform: none;
    font-weight: 400;
    padding: var(--label-padding);
    color: var(--clr-label);
    display: flex;
    align-items: center;
    position: relative;
  }
  cursor: pointer;
}

@media (hover: hover) and (pointer: fine) {
  .checkbox-custom {
    &:hover {
      .checkbox-icon:not(.checkbox-disabled) {
        &:after {
          opacity: 0.25;
        }
      }
    }
  }
}

.label-left {
  flex-direction: row-reverse;
  .checkbox-custom_label .checkbox-switch_label {
    padding: 0 0.25em 0 0;
  }
}

.label-left-full-width {
  width: 100%;
  flex-direction: row-reverse;
  .checkbox-switch_label {
    margin-right: auto;
    padding-left: 0;
  }
  .checkbox-custom_label {
    margin-right: auto;
    padding-left: 0;
  }
}

.checkbox-loading {
  pointer-events: none;
  border: none !important;
  &:before {
    content: '';
    height: 100%;
    width: 100%;
    background: var(--clr-black-1);
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
  }
  &:after {
    content: '';
    height: 100%;
    width: 100%;
    background: black;
    position: absolute;
    z-index: 2;
    top: 0;
    left: 0;
    animation: opacityPulse 2s infinite;
  }
  @keyframes opacityPulse {
    0% {
      opacity: 0.075;
    }
    50% {
      opacity: 0.25;
    }
    100% {
      opacity: 0.075;
    }
  }
}

.checkbox-btn_single {
  --clr-back: transparent;
  --clr-back-hover: var(--clr-black-4);
  --clr-back-active: var(--clr-black-4);

  --clr-border: var(--clr-back);
  --clr-border-hover: var(--clr-back);
  --clr-border-active: var(--clr-back);
}

.checkbox-btn_borderedsingle {
  --clr-back: transparent;
  --clr-back-hover: var(--clr-black-4);
  --clr-back-active: var(--clr-black-4);

  --clr-border: var(--clr-back);
  --clr-border-hover: var(--clr-back);
  --clr-border-active: var(--clr-back);
}
.checkbox-btn {
  --clr: var(--clr-white-0);
  --size: 1em;
  --padding: 0em 0.75em;
  --height: 2em;
  --clr-enabled: var(--clr-primary);
  --radius: 4px;
  --border-width: 0px;
  --clr-active_special: var(--clr-grey-2);
  --clr-back: transparent;
  --clr-back-hover: var(--clr-black-3);
  --clr-back-active: var(--clr-black-4);
  --clr-border: transparent;
  --clr-border-hover: var(--clr-back-hover);
  --clr-border-active: var(--clr-back-active);
  --align-items-checkbox-btn: center;
  --justify-content-checkbox-btn: center;
  &_bordered {
    --border-width: 2px;
    --clr-back: var(--clr-black-1-3);
    --clr-back-hover: var(--clr-black-1-3);
    --clr-back-active: var(--clr-black-1-4);
    --clr-border: var(--clr-black-3);
    --clr-border-hover: var(--clr-enabled);
    --clr-border-active: var(--clr-enabled);
  }
  &_active {
    z-index: 1;
    --clr-back: var(--clr-back-active) !important;
    --clr-border: var(--clr-border-active) !important;
    --clr-active_special: var(--clr-enabled);
  }

  color: var(--clr);
  background: var(--clr-back);
  outline: solid var(--clr-border) var(--border-width);
  border-radius: var(--radius);
  font-size: var(--size);
  line-height: 1;
  padding: var(--padding);
  height: var(--height);
  position: relative;
  cursor: pointer;
  transition: 0.15s;
  white-space: nowrap;
  display: flex;
  align-items: var(--align-items-checkbox-btn);
  justify-content: var(--justify-content-checkbox-btn);
  flex-shrink: 0;

  &_drag {
    --padding: 0.5em 0.65em;

    box-sizing: border-box;
    background-clip: padding-box;

    --border-width: 1px;
    --clr-border: var(--clr-black-0);
    --clr-border-hover: var(--clr-black-0);
    --clr-border-active: var(--clr-black-0);
    transition: 0s;
    border-radius: 0px;
    &:nth-child(1) {
      border-top-left-radius: var(--radius);
      border-bottom-left-radius: var(--radius);
    }
    &:last-child {
      border-top-right-radius: var(--radius);
      border-bottom-right-radius: var(--radius);
    }
  }
}

@media (hover: hover) and (pointer: fine) {
  .checkbox-btn {
    &:not(.checkbox-btn_active) {
      &:hover {
        --clr-back: var(--clr-back-hover) !important;
        --clr-border: var(--clr-border-hover) !important;
      }
    }
  }
}

.icon-shadow {
  &:before {
    @include stroke($color: var(--clr-black-0), $size: 2px);
  }
}

.checkbox-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--icon-size);
  line-height: 1;
  color: currentColor;
  border-radius: 4px;
  &:after {
    content: '';
    position: absolute;
    background: currentColor;
    border-radius: 4px;
    opacity: 0;
    height: 133%;
    width: 133%;
    transition: 0.25s;
  }
}

@media (hover: hover) and (pointer: fine) {
  .checkbox-icon {
    &:not(.checkbox-disabled) {
      &:hover {
        &:after {
          opacity: 0.25;
        }
      }
    }
  }
}

@media (hover: hover) and (pointer: fine) {
  .checkbox-switch {
    &:hover {
      cursor: pointer;
      .checkbox-switch_circle {
        --clr-circle: var(--clr-circle-hover);
        &:after {
          opacity: 0.3;
        }
      }
    }
  }
}

.switch_reversed {
  flex-direction: row-reverse;
}
.checkbox-switch {
  --height: 1.25em;

  --clr-label: var(--clr-grey-2);
  --label-padding: 0 0.5em;

  --clr-body: var(--clr-black-3);
  --clr-enabled: var(--clr-primary);
  --clr-body-active: var(--clr-primary-2);
  --body-height: 1.125em;
  --body-width: 2.5em;

  --clr-circle: var(--clr-grey-2);
  --clr-circle-hover: var(--clr-grey-3);
  --clr-circle-active: var(--clr-enabled);
  --circle-dimesions: var(--body-height);

  min-height: var(--height);
  flex: 0;

  display: flex;
  position: relative;
  align-items: center;

  &.checkbox-on {
    .checkbox-switch_circle {
      --clr-circle: var(--clr-circle-active);

      right: calc(-100% + var(--circle-dimesions));
    }
    .checkbox-switch_body {
      --clr-body: var(--clr-body-active);
    }
  }
  &_body {
    height: var(--body-height);
    width: var(--body-width);
    background: var(--clr-body);
    border-radius: 99px;
    position: relative;
    margin: 0 0.5em 0 0;
    transition: 0.22s;
  }
  &_circle {
    width: var(--circle-dimesions);
    height: var(--circle-dimesions);
    background: var(--clr-circle);
    z-index: 2;
    border-radius: 50%;
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    right: 0;
    transition: 0.22s ease-in-out;
    &:after {
      content: '';
      position: absolute;
      opacity: 0;
      width: inherit;
      height: inherit;
      transform: scale(1.5);
      border-radius: 50%;
      transition: 0.25s;
      background: var(--clr-circle);
    }
  }
  &_label {
    cursor: pointer;
    color: var(--clr-label);
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
